/** @jsxImportSource @emotion/react */
import { css, Theme } from '@emotion/react';
import React, { FC, useMemo, useRef } from 'react';
import styled from '@emotion/styled/macro';
import { useParams } from 'react-router-dom';
import isPropValid from '@emotion/is-prop-valid';

import { Flex, Box, Grid } from 'components/styled';
import Text from 'components/styled/Text';
import { default as TitleTextOrg } from 'components/styled/TitleText';
import { Wrapper, ResponsivePadder } from 'components/styled/Wrapper';
import Button, { NavButton, NavLinkButton } from 'components/styled/Button';
import ScrollToTopButton from 'components/floatingactions/ScrollToTopButton';
import { ListItem, UnorderedList } from 'components/styled/List';
import ModalDialog from 'components/modal';
import Paper from 'components/styled/Paper';
import ProductTab from 'components/tiles/productTab';

import { useActiveLicences } from 'api/publicApi';

import { usePageTitle } from 'hooks/seoHooks';
import useBreakpoint from 'hooks/useBreakpoint';
import useMapProductId from 'hooks/useMapProductId';

import { useLocalizedMultistring } from 'utils/localization';
import { ProductsTypesTuple } from 'utils/enumMaps';

import { useScrollToSection } from '../_utils';

import CountupInfoLiberec from './CountupInfoLiberec';
import homeBg from './homeBg.jpg';
import homeStudents from './homeStudents.jpg';
import homeWork from './homeWork.jpg';

const TitleText = styled(TitleTextOrg, {
	shouldForwardProp: isPropValid,
})<{ borderColor?: string }>`
	border-left: 4px solid
		${p => (p.borderColor ? p.borderColor : p.theme.colors.primary)};
	text-align: left;
	padding: 0 0 0 16px !important;
	margin-left: 0px !important;
`;

type ButtonProps = {
	height?: number;
	color?: string;
	variant?: 'primary' | 'outlined';
};

const NavLinkButtonArrow: React.FC<
	Omit<
		React.ComponentProps<typeof NavLinkButton>,
		'height' | 'color' | 'variant'
	> &
		ButtonProps
> = ({
	height = 50,
	children,
	variant = 'primary',
	color = variant === 'primary' ? 'white' : 'primary',
	...props
}) => {
	const diagonal = useMemo(() => height / Math.sqrt(2), [height]);
	const transY = (height - diagonal) / 2;

	return (
		<Flex overflow="hidden" minWidth="fit-content">
			<NavLinkButton
				{...props}
				height={height}
				max-height={height}
				variant={variant === 'outlined' ? 'outlined' : 'default'}
				minWidth={170}
				color={color}
				zIndex={2}
				css={(theme: Theme) => css`
					text-transform: uppercase;
					border-right: none;
					border-color: ${color};
					background-color: ${variant === 'primary'
						? theme.colors.primary
						: 'transparent'};
					&:hover {
						background-color: ${variant === 'primary'
							? theme.colors.primary
							: 'transparent'};
						color: ${
							// eslint-disable-next-line no-nested-ternary
							variant === 'primary'
								? 'white'
								: color === 'primary'
								? theme.colors.primary
								: 'white'
						};
					}
				`}
			>
				{children}
			</NavLinkButton>
			<Box
				width={diagonal}
				height={diagonal}
				bg={variant === 'outlined' ? 'transparent' : 'primary'}
				color={color}
				zIndex={1}
				css={css`
					border: ${variant === 'outlined' ? '1px solid' : 'none'};
					border-left: none;
					border-bottom: none;
					transform: translateX(-50%) translateY(${transY}px) rotate(45deg);
				`}
			/>
		</Flex>
	);
};

const LiberecHomepage: FC = () => {
	const localize = useLocalizedMultistring();

	// Page title
	usePageTitle(localize({ CZECH: 'Vítejte', ENGLISH: 'Welcome' }));

	const response = useActiveLicences();
	const mapProductId = useMapProductId();
	const licences = useMemo(
		() =>
			(response.data ?? []).sort(
				(a, b) =>
					ProductsTypesTuple.indexOf(mapProductId(a.id)) -
					ProductsTypesTuple.indexOf(mapProductId(b.id)),
			),
		[response.data, mapProductId],
	);

	const aboutSectionRef = useRef<HTMLDivElement>(null);
	const companySectionRef = useRef<HTMLDivElement>(null);
	const studentSectionRef = useRef<HTMLDivElement>(null);

	const { anchor } = useParams<{ anchor: string }>();

	let section: React.RefObject<HTMLDivElement> | null = null;
	switch (anchor) {
		case 'o-karierovem-centru':
			section = aboutSectionRef;
			break;
		case 'pro-zamestnavatele':
			section = companySectionRef;
			break;
		case 'pro-studenty':
			section = studentSectionRef;
			break;
	}

	useScrollToSection(section);

	const isCollapsed = useBreakpoint(2);
	const isMobile = useBreakpoint(0);

	return (
		<Wrapper color="white">
			<Flex
				alignItems="flex-end"
				css={css`
					background-image: url(${homeBg});
					background-size: cover;
					background-position: top center;
					background-repeat: no-repeat;
					width: 100%;
					min-height: 400px;
					max-height: 400px;
				`}
			>
				<Flex
					flexDirection="column"
					alignItems="flex-start"
					width={1}
					bg="primary"
					opacity={0.9}
				>
					<ResponsivePadder>
						<Flex
							justifyContent="space-between"
							alignItems="center"
							flexDirection={isCollapsed ? 'column' : 'row'}
						>
							<Text
								opacity={1}
								fontSize={isCollapsed ? '1.7em' : '1.8em'}
								fontWeight={600}
								textAlign={isCollapsed ? 'center' : 'left'}
								my={isCollapsed ? 2 : 'unset'}
							>
								{localize({
									CZECH:
										'JobTUL - kariérní portál Technické univerzity v Liberci',
									ENGLISH:
										'JobTUL - Career portal of the Technical University of Liberec',
								})}
							</Text>
							<Box color="white">
								<CountupInfoLiberec />
							</Box>
						</Flex>
					</ResponsivePadder>
				</Flex>
			</Flex>

			<Wrapper
				bg="white"
				color="text"
				css={css`
					overflow: hidden;
				`}
			>
				<ResponsivePadder py={5} ref={aboutSectionRef}>
					<Flex flexDirection={['column', 'row']}>
						<Box width={[1, 1 / 2]}>
							<TitleText>
								{localize({
									CZECH: 'Co to je JobTUL?',
									ENGLISH: 'What is JobTUL?',
								})}
							</TitleText>

							<Text>
								{localize({
									CZECH:
										'JobTUL je oficiální kariérní portál Technické univerzity v Liberci, který propojuje studenty a absolventy se zaměstnavateli. Přináší nové možnosti v oblasti rozvoje kariéry při studiu i po něm a hledání možností spolupráce s firmami.',
									ENGLISH:
										'JobTUL is the official career portal of the Technical University of Liberec, which connects its students and alumni with employers. It brings new opportunities in the field of career development during and after study and the search for opportunities for cooperation with companies.',
								})}
							</Text>
						</Box>

						<Box width={[1, 1 / 2]} ml={[0, 4]}>
							<TitleText>
								{localize({
									CZECH: 'O Kariérovém centru TUL',
									ENGLISH: 'About the Career Centre of the TUL',
								})}
							</TitleText>

							<Text>
								{localize({
									CZECH:
										'Jsme Kariérové centrum Technické univerzity v Liberci. Působíme jako součást akademické poradny a centra podpory. Kariérové centrum podporuje studenty a absolventy TUL na cestě ke spokojenosti v jejich budoucí i současné kariéře. Poskytované služby jsou zaměřeny na rozvoj schopností, dovedností a návyků v oblasti sebeprezentace s cílem zlepšit výchozí pozici na trhu práce.',
									ENGLISH:
										'We are the Career Centre of the Technical University of Liberec. We operate as part of an academic counselling and support centre. The Career Centre supports TUL students and alumni on their path to satisfaction in their current and future careers. The services provided are mainly focused on the development of personal abilities, skills and habits for example in the field of self-presentation in order to improve their starting position in the labour market.',
								})}
								<ModalDialog
									label={localize({
										CZECH: 'Více...',
										ENGLISH: 'Read more...',
									})}
									control={openModal => (
										<NavButton variant="text" onClick={openModal} p={0} ml={3}>
											<Text fontSize="lg" fontWeight="bold" as="span">
												{' '}
												{localize({
													CZECH: 'Více...',
													ENGLISH: 'Read more...',
												})}
											</Text>
										</NavButton>
									)}
								>
									{closeModal => (
										<Paper>
											<Flex px={4} py={3} flexDirection={['column', 'row']}>
												<Box>
													<Text fontSize="lg" fontWeight="bold">
														{localize({
															CZECH: 'Studentům poskytujeme:',
															ENGLISH: 'To students we provide:',
														})}
													</Text>
													<UnorderedList
														color="text"
														css={css`
															list-style: disc;
															li::before {
																content: none;
															}
														`}
													>
														<ListItem>
															{localize({
																CZECH:
																	'Individuální poradenství v oblasti profesního směřování (konzultace CV a vytvoření kvalitního motivačního dopisu)',
																ENGLISH:
																	'Individual counselling in the field of career guidance (CV consultation and creation of a quality cover letter)',
															})}
														</ListItem>
														<ListItem>
															{localize({
																CZECH: 'Pomoc při přípravě na vstupní pohovor',
																ENGLISH:
																	'Assistance in preparing for job interviews',
															})}
														</ListItem>
														<ListItem>
															{localize({
																CZECH: 'Rozvojové kurzy a workshopy',
																ENGLISH: 'Development courses and workshops',
															})}
														</ListItem>
														<ListItem>
															{localize({
																CZECH:
																	'Propojování se zaměstnavateli skrze kariérní portál JobTUL',
																ENGLISH:
																	'Connecting with employers through the JobTUL career portal',
															})}
														</ListItem>
														<ListItem>
															{localize({
																CZECH: 'Setkávání se zaměstnavateli',
																ENGLISH: 'Meetings with employers',
															})}
														</ListItem>
													</UnorderedList>
												</Box>
												<Box ml={[0, 4]}>
													<Text fontSize="lg" fontWeight="bold">
														{localize({
															CZECH: 'Zaměstnavatelům nabízíme:',
															ENGLISH: 'To employers we provide:',
														})}
													</Text>
													<UnorderedList
														color="text"
														css={css`
															list-style: disc;
															li::before {
																content: none;
															}
														`}
													>
														<ListItem>
															{localize({
																CZECH:
																	'Efektivní nábor kandidátů skrze kariérní portál JobTUL',
																ENGLISH:
																	'Effective recruitment of candidates through the career portal JobTUL',
															})}
														</ListItem>
														<ListItem>
															{localize({
																CZECH:
																	'Propagaci zaměstnavatele uvnitř univerzity prostřednictvím spolupráce s Kariérovým centrem TUL',
																ENGLISH:
																	'Promotion of the employer within the university through cooperation with the Career Centre',
															})}
														</ListItem>
														<ListItem>
															{localize({
																CZECH:
																	'Účast na událostech pořádaných pro studenty',
																ENGLISH:
																	'Participation in events organized for students',
															})}
														</ListItem>
														<ListItem>
															{localize({
																CZECH:
																	'Informační podporu při účasti na veletrzích pracovních příležitostí pro studenty různého oborového zaměření',
																ENGLISH:
																	'Information support for participation in career fairs for students of various specializations',
															})}
														</ListItem>
														<ListItem>
															{localize({
																CZECH: 'Odborné náborové a rozvojové služby.',
																ENGLISH:
																	'Professional recruitment and development services.',
															})}
														</ListItem>
													</UnorderedList>
												</Box>
											</Flex>
											<Box>
												<Button onClick={closeModal} variant="outlined">
													{localize({
														CZECH: 'Zpět',
														ENGLISH: 'Go back',
													})}
												</Button>
											</Box>
										</Paper>
									)}
								</ModalDialog>
							</Text>
						</Box>
					</Flex>
				</ResponsivePadder>
				<ResponsivePadder ref={studentSectionRef} pb={0}>
					<Flex flexDirection={['column', 'row']}>
						<Flex
							width={[1, 1 / 2]}
							flexDirection="column"
							justifyContent="space-between"
							bg="primary"
							color="white"
							p={4}
						>
							<TitleText color="white" borderColor="white">
								{localize({
									CZECH: 'Studentům a absolventům JobTUL nabízí:',
									ENGLISH: 'JobTUL offers students and graduates:',
								})}
							</TitleText>
							<Text>
								{localize({
									CZECH:
										'Hledáte zkušenosti, které si uvedete do svého CV? Chcete propojit získané teoretické poznatky s praxí? Zajímá Vás, jaké možnosti nabízí trh práce právě Vám? Pokud ano, máme portál JobTUL přímo pro Vás! JobTUL je online nástroj pro Vaši kariéru, který Vám nabízí moře příležitostí na jednom místě. Umožňuje hledat mezi kariérními příležitostmi od zaměstnavatelů. Vybírat můžete mezi:',
									ENGLISH:
										'Are you looking for an experience that you can include in your CV? Do you want to connect your acquired theoretical knowledge with practice? Are you interested in what possibilities the labour market offers you? If so, we have the JobTUL portal just for you! JobTUL is an online tool for your career which offers you  many opportunities in just one place. It allows you to search through career opportunities from a large number of employers. You can choose between:',
								})}
							</Text>

							<UnorderedList
								color="white"
								css={css`
									list-style: disc inside;
									li::before {
										content: none;
									}
								`}
							>
								<ListItem>
									{localize({
										CZECH: 'plnými a zkrácenými úvazky,',
										ENGLISH: 'full-time and part-time jobs,',
									})}
								</ListItem>
								<ListItem>
									{localize({
										CZECH: 'stážemi/povinnými studijními praxemi,',
										ENGLISH: 'internships / compulsory study practices,',
									})}
								</ListItem>
								<ListItem>
									{localize({
										CZECH: 'návrhy témat bakalářských a diplomových prací.',
										ENGLISH: `proposals for bachelor's and master's thesis topics.`,
									})}
								</ListItem>
							</UnorderedList>
							<Flex flexDirection={['column', 'column', 'row']}>
								<ModalDialog
									label={localize({
										CZECH: 'Více informací',
										ENGLISH: 'More information',
									})}
									control={openModal => (
										<NavButton
											variant="outlined"
											onClick={openModal}
											color="white"
											height={50}
											minWidth="fit-content"
											mr={[0, 0, 3]}
											mb={[3, 3, 0]}
											css={css`
												border: 1px solid white;
											`}
										>
											<Text
												as="span"
												css={css`
													text-transform: uppercase;
												`}
											>
												{localize({
													CZECH: 'Více informací',
													ENGLISH: 'More information',
												})}
											</Text>
										</NavButton>
									)}
								>
									{closeModal => (
										<Paper>
											<Box px={[1, 4]} py={3}>
												<TitleText>
													{localize({
														CZECH: 'Student',
														ENGLISH: 'Student',
													})}
												</TitleText>
												<Text fontWeight="bold">
													{localize({
														CZECH: 'Registrujte se a představte se',
														ENGLISH: 'Register and introduce yourself',
													})}
												</Text>
												<Text>
													{localize({
														CZECH:
															'Vytvořte si kariérní profil a efektivní online CV v jednom. Představte sami sebe i svoje kariérní preference, zkušenosti, znalosti a dovednosti.',
														ENGLISH:
															'Create your career profile and an effective online CV in one. Introduce yourself and your career preferences, experience, knowledge and obtained skills.',
													})}
												</Text>
												<Text fontWeight="bold">
													{localize({
														CZECH: 'Prohlédněte si nabídky zaměstnavatelů',
														ENGLISH: `View employers' vacancies`,
													})}
												</Text>
												<Text>
													{localize({
														CZECH:
															'V portálu najdete pouze studentské a absolventské nabídky! JobTUL je místo, na kterém naleznete především juniorní pozice, které jsou vhodné pro studenty s minimální praxí.',
														ENGLISH:
															'In the career portal you will find vacancies which correspond to the experience of students and alumni! JobTUL is a place where you will find mainly junior positions that are suitable for students with minimal job experience.',
													})}
												</Text>
												<Text fontWeight="bold">
													{localize({
														CZECH: 'Nechte si doporučit ideální nabídku',
														ENGLISH: 'Find an ideal vacancy',
													})}
												</Text>
												<Text>
													{localize({
														CZECH:
															'JobTUL porovnává Vaše preference a dovednosti s vypsanými inzeráty a automaticky Vám doporučuje odpovídající pracovní nabídky a zaměstnavatele. Generované nabídky splňují Vámi zadané preference a kritéria.',
														ENGLISH:
															'JobTUL compares your preferences and skills with employers’ advertisements and automatically recommends suitable job vacancies and employers. Generated vacancies will meet your specified preferences and criteria.b offers and employers. Generated offers will meet your specified preferences and criteria.',
													})}
												</Text>
												<Text fontWeight="bold">
													{localize({
														CZECH: 'Rozvíjejte se',
														ENGLISH: 'Develop yourself',
													})}
												</Text>
												<Text>
													{localize({
														CZECH:
															'Portál Vás průběžně informuje, které požadavky v nabídkách splňujete a které nikoliv. Díky tomu snadno získáte přehled o tom, v čem se zdokonalit a dále rozvíjet, aby o Vás na trhu práce byl ještě větší zájem.',
														ENGLISH:
															'The portal will keep you informed of which requirements you meet in the vacancies and which you do not. Thanks to this information, you will easily get an overview of what to improve on and further develop in order to be even more suitable for employers in the labour market.',
													})}
												</Text>
												<Text fontWeight="bold">
													{localize({
														CZECH: 'Šetřete svůj čas',
														ENGLISH: 'Save your time',
													})}
												</Text>
												<Text>
													{localize({
														CZECH:
															'Získání pracovních zkušeností a ideálního tématu závěrečné práce nikdy nebylo snazší! Na odpovídající nabídku můžete nyní reagovat stisknutím jediného tlačítka. Navíc, pokud Váš profil zaujme zaměstnavatele, mohou Vás sami aktivně oslovit a nabídnout Vám nabídku připravenou na míru.',
														ENGLISH:
															'Gaining relevant work experience and the ideal topic for your final thesis has never been easier! You can now react to the ideal vacancy with just the press of a button. In addition, if your profile attracts employers, they can contact you and make you a tailor-made offer.',
													})}
												</Text>

												<Flex
													justifyContent="space-between"
													flexDirection={['column', 'row']}
													alignItems="center"
													mt={4}
												>
													<Button
														width={['100%', 'unset']}
														mb={[3, 0]}
														onClick={closeModal}
														variant="outlined"
														padding="12px"
													>
														{localize({
															CZECH: 'Zpět',
															ENGLISH: 'Go back',
														})}
													</Button>

													{isMobile ? (
														<NavLinkButton
															width={1}
															variant="primary"
															to="/login"
														>
															{localize({
																CZECH: 'Zaregistrovat se',
																ENGLISH: 'Register',
															})}
														</NavLinkButton>
													) : (
														<NavLinkButtonArrow
															width={190}
															variant="primary"
															to="/login"
														>
															{localize({
																CZECH: 'Zaregistrovat se',
																ENGLISH: 'Register',
															})}
														</NavLinkButtonArrow>
													)}
												</Flex>
											</Box>
										</Paper>
									)}
								</ModalDialog>
								<NavLinkButtonArrow
									width={190}
									variant="outlined"
									color="white"
									to="/login"
								>
									{localize({
										CZECH: 'Zaregistrovat se',
										ENGLISH: 'Register',
									})}
								</NavLinkButtonArrow>
							</Flex>
						</Flex>
						<Flex
							width={[1, 1 / 2]}
							height={[300, 'unset']}
							css={css`
								background-image: url(${homeStudents});
								background-size: cover;
								background-repeat: no-repeat;
								background-position: center;
							`}
						/>
					</Flex>
				</ResponsivePadder>
				<ResponsivePadder ref={companySectionRef} pb={5}>
					<Flex flexDirection={['column', 'row']}>
						<Flex
							order={[0, 1]}
							width={[1, 1 / 2]}
							flexDirection="column"
							justifyContent="space-between"
							//TODO - add to theme?
							bg="#EEE"
							color="primary"
							p={4}
						>
							<TitleText color="primary">
								{localize({
									CZECH: 'Zaměstnavatelům JobTUL nabízí:',
									ENGLISH: 'JobTUL offers employers',
								})}
							</TitleText>
							<Text>
								{localize({
									CZECH:
										'Chcete efektivně nabírat mladé talenty z řad studentů a absolventů Technické univerzity v Liberci a budovat svůj brand zaměstnavatele na univerzitní půdě? Vytvořte si na portálu JobTUL svůj profil, skrze který naleznete kandidáty podle Vašich aktuálních náborových potřeb! JobTUL Vám nabízí jasně definovanou cílovou skupinu kandidátů a jejich efektivní vyhledávání. Vybrat si můžete různé balíčky a podle toho volit rozsah všech aktivit na JobTULu. Na JobTULu můžete zveřejňovat:',
									ENGLISH:
										'Do you want to recruit young talent among students and alumni of the Technical University of Liberec effectively and build your employer brand on the university campus? Create your own profile on the JobTUL portal, through which you will find candidates according to your current recruitment needs! JobTUL offers you a clearly defined target group of candidates as well as an effective search tool to match you to the most suitable candidates. You can choose from different packages and select the scope of your activities on JobTUL according to your needs and requirements.  On JobTUL you can publish:',
								})}
							</Text>
							<UnorderedList
								color="primary"
								css={css`
									list-style: disc inside;

									li::before {
										content: none;
									}
								`}
							>
								<ListItem>
									{localize({
										CZECH: 'pracovní nabídky,',
										ENGLISH: 'job vacancies,',
									})}
								</ListItem>
								<ListItem>
									{localize({
										CZECH: 'nabídky stáží,',
										ENGLISH: 'internship vacancies,',
									})}
								</ListItem>
								<ListItem>
									{localize({
										CZECH: 'témata závěrečných prací (BP, DP)',
										ENGLISH: 'final thesis topics (bachelor, masters).',
									})}
								</ListItem>
							</UnorderedList>
							<Flex flexDirection={['column', 'column', 'row']}>
								<ModalDialog
									customCss={() => css`
										width: 80%;

										@media (max-width: 1565px) {
											width: 95%;
										}

										@media (max-width: 800px) {
											width: 90%;
										}
										@media (max-width: 550px) {
											width: 100%;
											padding: 0px;
										}
									`}
									label={localize({
										CZECH: 'Více informací',
										ENGLISH: 'More information',
									})}
									control={openModal => (
										<NavButton
											variant="outlined"
											onClick={openModal}
											height={50}
											minWidth="fit-content"
											mr={[0, 0, 3]}
											mb={[3, 3, 0]}
										>
											<Text
												as="span"
												css={css`
													text-transform: uppercase;
												`}
											>
												{localize({
													CZECH: 'Více informací',
													ENGLISH: 'More information',
												})}
											</Text>
										</NavButton>
									)}
								>
									{closeModal => (
										<Paper>
											<Box px={[1, 4]} py={3}>
												<TitleText>
													{localize({
														CZECH: 'Zaměstnavatel',
														ENGLISH: 'Employer',
													})}
												</TitleText>
												<Text fontWeight="bold">
													{localize({
														CZECH: 'Prezentace zaměstnavatele',
														ENGLISH: `Employer's presentation`,
													})}
												</Text>
												<Text>
													{localize({
														CZECH:
															'Prostřednictvím zaměstnavatelského profilu máte jedinečnou příležitost budovat svou značku zaměstnavatele. JobTUL je online nástroj, pomocí kterého budete prezentovat Vaši firemní kulturu, popíšete průběh přijímacího procesu či definujete své požadavky na uchazeče.',
														ENGLISH: `Through an employer's profile, you have a unique opportunity to build your employer brand. JobTUL is an online tool with which you can present your company culture, describe your recruitment process or define your requirements for applicants.`,
													})}
												</Text>
												<Text fontWeight="bold">
													{localize({
														CZECH: 'Cílená inzerce',
														ENGLISH: 'Targeted advertising',
													})}
												</Text>
												<Text>
													{localize({
														CZECH:
															'Inzerujte Vaše aktuálně obsazované pracovní pozice, stáže, praxe či návrhy témat závěrečných prací na portálu JobTUL, a to cíleně na relevantní kandidáty.',
														ENGLISH:
															'Advertise your current job openings, internships, or final thesis topics on the JobTUL portal specifically for relevant candidates.',
													})}
												</Text>
												<Text fontWeight="bold">
													{localize({
														CZECH: 'Doporučování ideálních kandidátů',
														ENGLISH: 'Recommendation of ideal candidates',
													})}
												</Text>
												<Text>
													{localize({
														CZECH:
															'JobTUL v reálném čase porovnává Vaše požadavky v pracovních inzerátech s uživatelskými profily a doporučuje Vám nejen kandidáty, kteří odpovídají Vašim kritériím, ale i ty uchazeče, kteří mají potenciál k rozvoji. Relevantní pozice portál následně doporučuje vhodným kandidátům, kteří Vás mohou sami oslovit.',
														ENGLISH:
															'JobTUL compares your requirements in job advertisements with user profiles in real time and recommends not only candidates who meet your criteria, but also those candidates who have the potential to meet them in the future. The portal then recommends the relevant position to suitable candidates who can contact you themselves.',
													})}
												</Text>
												<Text fontWeight="bold">
													{localize({
														CZECH: 'Vyhledávejte uchazeče v databázi',
														ENGLISH: 'Search for candidates in the database',
													})}
												</Text>
												<Text>
													{localize({
														CZECH:
															'Vstupujte aktivně do databáze registrovaných studentů či absolventů a oslovte přesně ty, kteří splňují Vámi definované požadavky. JobTUL umožňuje filtraci registrovaných kandidátů, máte tedy jistotu, že vyhledáte toho pravého uchazeče.',
														ENGLISH:
															'On JobTUL you can also access the database of registered students or alumni and address only those who meet your defined requirements. The portal also allows you to filter registered candidates, so you can be sure that you will find the right candidate.',
													})}
												</Text>

												<Grid
													width={1}
													gridGap={3}
													textAlign="left"
													mt={2}
													css={() => css`
														grid-template-columns: 1fr 1fr 1fr 1fr !important;

														@media (max-width: 1400px) {
															grid-template-columns: 1fr 1fr !important;
														}

														@media (max-width: 850px) {
															grid-template-columns: 1fr !important;
														}
													`}
												>
													{(licences ?? []).map(p => (
														<ProductTab
															key={p.id}
															product={p}
															variant="PREVIEW"
															productTypeById={mapProductId(p.id)}
														/>
													))}
												</Grid>
												<Flex
													justifyContent="space-between"
													flexDirection={['column', 'row']}
													alignItems="center"
													mt={4}
												>
													<Button
														width={['100%', 'unset']}
														mb={[3, 0]}
														onClick={closeModal}
														variant="outlined"
														padding="12px"
													>
														{localize({
															CZECH: 'Zpět',
															ENGLISH: 'Go back',
														})}
													</Button>
													{isMobile ? (
														<NavLinkButton
															width={1}
															variant="primary"
															to="/registration/company"
														>
															{localize({
																CZECH: 'Zaregistrovat se',
																ENGLISH: 'Register',
															})}
														</NavLinkButton>
													) : (
														<NavLinkButtonArrow
															width={190}
															variant="primary"
															to="/registration/company"
														>
															{localize({
																CZECH: 'Zaregistrovat se',
																ENGLISH: 'Register',
															})}
														</NavLinkButtonArrow>
													)}
												</Flex>
											</Box>
										</Paper>
									)}
								</ModalDialog>
								<NavLinkButtonArrow
									width={190}
									variant="outlined"
									to="/registration/company"
								>
									{localize({
										CZECH: 'Zaregistrovat se',
										ENGLISH: 'Register',
									})}
								</NavLinkButtonArrow>
							</Flex>
						</Flex>

						<Flex
							width={[1, 1 / 2]}
							height={[300, 'unset']}
							css={css`
								background-image: url(${homeWork});
								background-size: cover;
								background-repeat: no-repeat;
								background-position: center;
							`}
						/>
					</Flex>
				</ResponsivePadder>
			</Wrapper>
			<Wrapper bg="lightGrey">
				<ResponsivePadder
					py={3}
					flexDirection={['column', 'row']}
					justifyContent="space-between"
					alignItems={['flex-start', 'center']}
				>
					<Box color="text" pl={[4, 0]}>
						<Text fontSize="lg" fontWeight="bold" as="span">
							{localize({
								CZECH: 'Potřebujete',
								ENGLISH: 'Do you',
							})}
						</Text>
						<Text fontSize="lg" fontWeight="bold" as="span" color="primary">
							{' '}
							{localize({
								CZECH: 'poradit?',
								ENGLISH: 'need advice?',
							})}
						</Text>
						<Text>
							{localize({
								CZECH: 'Neváhajte nás kontaktovat na našem portálu',
								ENGLISH: 'Do not hesitate to contact us on our portal',
							})}
						</Text>
					</Box>

					<NavLinkButtonArrow
						ml={[4, 0]}
						width={190}
						to=""
						onClick={() =>
							window.location.replace(
								'http://apc.tul.cz/profesni-karierove-poradenstvi/',
							)
						}
					>
						{localize({
							CZECH: 'kontaktovat',
							ENGLISH: 'contact',
						})}
					</NavLinkButtonArrow>
				</ResponsivePadder>
			</Wrapper>

			<ScrollToTopButton />
		</Wrapper>
	);
};

export default LiberecHomepage;
